import { inject } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { FieldDefinition, localizeWithFallback, FieldBuilder, MenuCategories, MenuSubcategories, MENU_CATEGORY_KEY, MENU_SUBCATEGORY_KEY, MENU_NAME_SUFFIX, IModal, createLocalizationKey, ZendraLocalizationServices,  ActionOverRecordTypes, IActionsSchema, triggerAction, createFormModalConfigExtraLarge, modalAction, createFormModalConfig, checkboxFormatter, } from '@zendra/shared';
import { of } from 'rxjs';
import { ForestrySituationService, LocationService, PropertyService, ReasonService, RegionService, SoilUseService, StandTypeService, SubregionService, ZoneService } from 'cbd-proxies';
import { PlantationPlanningOrchestratorService, StandPlanningOrchestratorService } from 'orchestrator';
import { StandCreationForm } from './stand-creation-form/stand-creation-form';
import { Cycle } from '../cycle/cycle'
import { ActionCreatePlantation } from './action-create-plantation/action-create-plantation';
import { StandSoil } from '../stand-soil/stand-soil';
import { StandAreaMaintenance } from '../stand-area-maintenance/stand-area-maintenance';
import { ActionFirstCycleRotation } from './action-first-cycle-rotation/action-first-cycle-rotation';
import { StandPerRegister } from '../stand-per-register/stand-per-register';
import { ActionDeactivateStand } from './action-deactivate-stand/action-deactivate-stand';

FieldBuilder.setNamespace('Planning');

const locationIsActiveField = FieldBuilder
  .dropdown('locationIsActive', 'Entity.Location.IsActive')
  .options([
    { key: 'true', value: 'Yes' },
    { key: 'false', value: 'No' },
  ])
  .optionsLoader(() => {
    const localizationService = inject(LocalizationService);
    return of([
      { key: 'true', value: localizeWithFallback(localizationService, 'AbpUi::Yes') },
      { key: 'false', value: localizeWithFallback(localizationService, 'AbpUi::No') },
    ]);
  })
  .prefilter({ queryKey: 'locationIsActive', defaultValue: 'true', enforceOnQueries: false })
  .readOnly()
  .formatter((_row: number, _cell: number, _value: unknown, _colDef: unknown, dataContext: any, _grid: any) => {
    const value = dataContext?.location?.isActive ?? dataContext?.locationIsActive ?? _value;
    return checkboxFormatter(_row, _cell, value, _colDef as any, dataContext, _grid);
  })
  .build();

locationIsActiveField.column.params = {
  ...locationIsActiveField.column.params,
  queryFieldSorter: 'location.isActive',
};

export const STAND_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .text('name', 'Entity.Stand.Name')
    .required()
    .minLength(1)
    .maxLength(100)
    .build(),
  FieldBuilder
    .number('standArea', 'Entity.Stand.StandArea')
    .required()
    .decimal(2,10)
    .build(),
  FieldBuilder
    .number('gisArea', 'Entity.Stand.GisArea')
    .required()
    .decimal(2,10)
    .build(),
  FieldBuilder
    .checkbox('isStrategic', 'Entity.Stand.IsStrategic')
    .build(),
  FieldBuilder
    .checkbox('hasNearbyPopulation', 'Entity.Stand.HasNearbyPopulation')
    .build(),
  FieldBuilder
    .number('currentCycle', 'Entity.Stand.CurrentCycle')
    .readOnly()
    .build(),
  FieldBuilder
    .number('currentRotation', 'Entity.Stand.CurrentRotation')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isPefcCertified', 'Entity.Stand.IsPefcCertified')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isFscCertified', 'Entity.Stand.IsFscCertified')
    .readOnly()
    .build(),
  FieldBuilder // TODO: when the orchestrator is implemented, refactor this nested logic into it
    .foreignKey('deactivationReasonId', 'Entity.Stand.DeactivationReason', ReasonService)
    .display('name')
    .keyField('id')
    .readOnly()
    .build(),
  FieldBuilder
    .date('deactivationDate', 'Entity.Stand.DeactivationDate')
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('forestrySituationId', 'Entity.Stand.ForestrySituation', ForestrySituationService)
    .required()
    .displayFromNested('forestrySituation.name')
    .sortBy('forestrySituation.name')
    .build(),
  FieldBuilder
    .foreignKey('previousSoilUseId', 'Entity.Stand.PreviousSoilUse', SoilUseService)
    .queryFilter({ soilUseGroupName: 'Uso previo' })
    .displayFromNested('previousSoilUse.name')
    .sortBy('previousSoilUse.name')
    .build(),
  FieldBuilder
    .foreignKey('locationId', 'Entity.Location', LocationService)
    .required()
    .displayFromNested('location.name')
    .sortBy('location.name')
    .hideInChildForms()
    .build(),
  FieldBuilder
    .foreignKey('standTypeId', 'Entity.StandType', StandTypeService)
    .required()
    .displayFromNested('standType.name')
    .sortBy('standType.name')
    .build(),
  FieldBuilder
    .foreignKey('regionId', 'Entity.Region', RegionService)
    .displayFromNested('property.zone.subregion.region.name')
    .sortBy('property.zone.subregion.region.name')
    .prefilter({ multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('subregionId', 'Entity.Subregion', SubregionService)
    .displayFromNested('property.zone.subregion.name')
    .sortBy('property.zone.subregion.name')
    .prefilter({ dependsOn: ['regionId'], multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('zoneId', 'Entity.Zone', ZoneService)
    .displayFromNested('property.zone.name')
    .sortBy('property.zone.name')
    .prefilter({ dependsOn: ['subregionId'], multiple: true })
    .readOnly()
    .build(),
  FieldBuilder
    .foreignKey('propertyId', 'Entity.Property', PropertyService)
    .required()
    .displayFromNested('property.name')
    .sortBy('property.name')
    .prefilter({ dependsOn: ['zoneId'], multiple: true })
    .build(),
  locationIsActiveField,
];

const namespace = ZendraLocalizationServices.Planning;
const standLocalizationKey = createLocalizationKey('Entity.Stand', namespace);
const standAsideDataKeys = {
  name: createLocalizationKey('Entity.Stand.Name', namespace),
  currentCycle: createLocalizationKey('Entity.Stand.CurrentCycle', namespace),
  currentRotation: createLocalizationKey('Entity.Stand.CurrentRotation', namespace),
  standArea: createLocalizationKey('Entity.Stand.StandArea', namespace),
};

export const STAND_ACTION_IDS = {
  activateStandTrigger: 0,
  createPlantation: 1,
  undoCreateFirstCycleRotation: 2,
  firstCycleRotation: 3,
  deactivateStandModal: 4,
};

const ACTION_CREATE_PLANTATION_WRAPPER: IModal = createFormModalConfigExtraLarge({
  component: ActionCreatePlantation,
  title: createLocalizationKey('Action.Modal.CreatePlantation', namespace),
  beforeOpen: ({ selectedRecordIds }) => {
    const standId = selectedRecordIds[0];

    if (!standId) {
      return;
    }

    return inject(PlantationPlanningOrchestratorService).ensureCanStartCreatePlantationFromStand(standId);
  },
  asideConfigFactory: ({ selectedRecords }) => {
    const selectedStand = selectedRecords[0];

    if (!selectedStand) {
      return { show: false, data: {} };
    }

    return {
      show: true,
      title: standLocalizationKey,
      data: {
        [standAsideDataKeys.name]: selectedStand.name,
        [standAsideDataKeys.currentCycle]: selectedStand.currentCycle,
        [standAsideDataKeys.currentRotation]: selectedStand.currentRotation,
        [standAsideDataKeys.standArea]: selectedStand.standArea,
      },
    };
  },
});

const ACTION_FIRST_CYCLE_ROTATION_WRAPPER: IModal = createFormModalConfig({
  component: ActionFirstCycleRotation,
  title: createLocalizationKey('Action.Modal.FirstCycleRotation', namespace),
  subTitle: createLocalizationKey('Entity.Stand', namespace),
  asideConfig: { show: false, data: {}, title: '' },
});

const ACTION_DEACTIVATE_STAND_WRAPPER: IModal = createFormModalConfig({
  component: ActionDeactivateStand,
  title: createLocalizationKey('Action.Modal.DeactivateStand', namespace),
  subTitle: createLocalizationKey('Entity.Stand', namespace),
  beforeOpen: ({ selectedRecordIds }) => {
    const standId = selectedRecordIds[0];

    if (!standId) {
      return;
    }

    return inject(StandPlanningOrchestratorService).ensureCanStartDeactivateStand(standId);
  },
});

export const STAND_ACTIONS_CONFIG: IActionsSchema = {
  singleRecord: [
    modalAction(
      STAND_ACTION_IDS.createPlantation,
      'Action.Modal.CreatePlantation',
      ActionOverRecordTypes.SingleRecord,
      ACTION_CREATE_PLANTATION_WRAPPER,
      namespace
    ),
    triggerAction(
      STAND_ACTION_IDS.activateStandTrigger,
      'Action.Trigger.ActivateStand',
      ActionOverRecordTypes.SingleRecord,
      namespace,
    ),
    triggerAction(
      STAND_ACTION_IDS.undoCreateFirstCycleRotation,
      'Action.Trigger.UndoCreateFirstCycleAndRotation',
      ActionOverRecordTypes.SingleRecord,
      namespace,
    ),
    modalAction(
      STAND_ACTION_IDS.deactivateStandModal,
      'Action.Modal.DeactivateStand',
      ActionOverRecordTypes.SingleRecord,
      ACTION_DEACTIVATE_STAND_WRAPPER,
      namespace
    ),
  ],
  multipleRecord: [
    modalAction(
      STAND_ACTION_IDS.firstCycleRotation,
      'Action.Modal.FirstCycleRotation',
      ActionOverRecordTypes.MultipleRecord,
      ACTION_FIRST_CYCLE_ROTATION_WRAPPER,
      namespace
    )
  ]
};

export const WRAPPER_CONFIG = createFormModalConfigExtraLarge({
  component: StandCreationForm,
  title: createLocalizationKey('TableNewRegistry', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Stand.CreateStandTitle', ZendraLocalizationServices.LanguageService)
});

export const STAND_CONFIG = {
  entityName: 'Stand',
  localizationKey: 'Planning::Entity.Stand',
  permission: 'PlanningLocation.Stands',
  fields: STAND_FIELDS,
  actions: STAND_ACTIONS_CONFIG,
  actionIds: STAND_ACTION_IDS,
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.TablesPlanning + MENU_NAME_SUFFIX,
    children: [
      {
        entityName: 'Cycle',
        foreignKeyField: 'standLocationId',
        parentKeyField: 'locationId',
        localizationKey: 'Geographic::Entity.Cycle',
        component: Cycle,
      },
      {
        entityName: 'StandSoil',
        foreignKeyField: 'standLocationId',
        parentKeyField: 'locationId',
        localizationKey: 'Geographic::Entity.StandSoil',
        component: StandSoil,
      },
      {
        entityName: 'StandAreaMaintenance',
        foreignKeyField: 'standLocationId',
        parentKeyField: 'locationId',
        localizationKey: 'Geographic::Entity.StandAreaMaintenance',
        component: StandAreaMaintenance,
      },
      {
        entityName: 'StandPerRegister',
        foreignKeyField: 'standId',
        localizationKey: 'Planning::Entity.StandPerRegister',
        component: StandPerRegister,
      }
    ]
};
